import api from '../relevanter/services/api';

export interface AreaOption {
  id: string;
  name: string;
  countryId?: string;
  areas?: AreaOption[];
}

export interface IndustryOption {
  id: string;
  name: string;
  industries?: IndustryOption[];
}

export interface SpecializationOption {
  id: string;
  name: string;
  roles?: SpecializationOption[];
}

export interface MetroStationOption {
  id: string;
  name: string;
}

export interface MetroLineOption {
  id: string;
  name: string;
  hexColor?: string;
  stations: MetroStationOption[];
}

export interface MetroCityOption {
  id: string;
  name: string;
  lines: MetroLineOption[];
}

// === localStorage-кеш с TTL для статических словарей HH ===
const LS_PREFIX = 'hh_dict_';
const LS_TTL_MS = 24 * 60 * 60 * 1000; // 24 часа

function lsGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { data: T; expiresAt: number };
    if (Date.now() >= entry.expiresAt) {
      localStorage.removeItem(LS_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function lsSet<T>(key: string, data: T): void {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify({ data, expiresAt: Date.now() + LS_TTL_MS }));
  } catch {
    // localStorage full или недоступен — не критично, in-memory кеш всё равно работает
  }
}

let cachedAreas: AreaOption[] | null = null;
let cachedAreasTree: AreaOption[] | null = null;
let cachedAreasTreeRaw: AreaOption[] | null = null;
let cachedIndustries: IndustryOption[] | null = null;
let cachedSpecializations: SpecializationOption[] | null = null;
let cachedMetroCities: MetroCityOption[] | null = null;
let cachedWorkSchedules: Array<{id: string; name: string}> | null = null;
let cachedCurrencies: CurrencyOption[] | null = null;
let isLoading = false;
let isLoadingAreasTree = false;
let isLoadingAreasTreeRaw = false;
let isLoadingIndustries = false;
let isLoadingSpecializations = false;
let isLoadingMetro = false;
let isLoadingWorkSchedules = false;
let isLoadingCurrencies = false;
let pendingCurrenciesPromise: Promise<CurrencyOption[]> | null = null;
let pendingPromise: Promise<AreaOption[]> | null = null;
let pendingAreasTreePromise: Promise<AreaOption[]> | null = null;
let pendingAreasTreeRawPromise: Promise<AreaOption[]> | null = null;
let pendingIndustriesPromise: Promise<IndustryOption[]> | null = null;
let pendingSpecializationsPromise: Promise<SpecializationOption[]> | null = null;
let pendingMetroPromise: Promise<MetroCityOption[]> | null = null;
let pendingWorkSchedulesPromise: Promise<Array<{id: string; name: string}>> | null = null;

const flattenAreas = (items: any[]): AreaOption[] => {
  const result: AreaOption[] = [];
  const traverse = (nodes: any[]) => {
    nodes?.forEach((node) => {
      if (node && typeof node.id !== 'undefined' && node.name) {
        result.push({ id: String(node.id), name: String(node.name) });
      }
      if (Array.isArray(node.areas) && node.areas.length > 0) {
        traverse(node.areas);
      }
    });
  };
  traverse(items);
  return result;
};

const preserveAreasTree = (items: any[]): AreaOption[] => {
  return items.map((item) => ({
    id: String(item.id),
    name: String(item.name),
    countryId: item.countryId ? String(item.countryId) : undefined,
    areas: Array.isArray(item.areas) && item.areas.length > 0
      ? preserveAreasTree(item.areas)
      : undefined
  }));
};

const CITIES_TO_MERGE: [string, string, string, string][] = [
  ['Москва',          '1',   'Московская область',    '2019'],
  ['Санкт-Петербург', '2',   'Ленинградская область', '145'],
  ['Киев',            '115', 'Киевская область',      '2164'],
];

const normalizeAreasHierarchy = (list: AreaOption[]): AreaOption[] => {
  if (!Array.isArray(list) || list.length === 0) return list;

  const extractById = (id: string): AreaOption | null => {
    const idx = list.findIndex((a) => String(a?.id) === id);
    if (idx >= 0) {
      const [item] = list.splice(idx, 1);
      return item;
    }
    return null;
  };

  const extractByName = (name: string): AreaOption | null => {
    const lower = name.toLowerCase();
    const idx = list.findIndex((a) => typeof a?.name === 'string' && a.name.toLowerCase() === lower);
    if (idx >= 0) {
      const [item] = list.splice(idx, 1);
      return item;
    }
    return null;
  };

  for (const [cityName, cityId, regionName, _regionId] of CITIES_TO_MERGE) {
    const city = extractByName(cityName) || extractById(cityId);
    if (!city) continue;

    const regionLower = regionName.toLowerCase();
    const region = list.find((a) => typeof a?.name === 'string' && a.name.toLowerCase() === regionLower);

    if (region) {
      const children = Array.isArray(region.areas) ? [...region.areas] : [];
      region.areas = [city, ...children].sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }));
    } else {
      list.unshift(city);
    }
  }

  return list;
};

export const fetchHHAreas = async (): Promise<AreaOption[]> => {
  if (cachedAreas) {
    return cachedAreas;
  }

  // localStorage-кеш
  const fromLs = lsGet<AreaOption[]>('areas_flat');
  if (fromLs) {
    cachedAreas = fromLs;
    return fromLs;
  }

  if (pendingPromise) {
    return pendingPromise;
  }

  const request = async () => {
    isLoading = true;
    try {
      const response = await api.get('/hh/dictionaries/areas', { params: { locale: 'RU' } });
      const json = response.data;
      const items = Array.isArray(json.items) ? json.items : [];
      const areas = flattenAreas(items);
      cachedAreas = areas;
      lsSet('areas_flat', areas);
      return areas;
    } finally {
      isLoading = false;
      pendingPromise = null;
    }
  };

  pendingPromise = request();
  return pendingPromise;
};

const fetchHHAreasTreeRaw = async (): Promise<AreaOption[]> => {
  if (cachedAreasTreeRaw) return cachedAreasTreeRaw;

  // localStorage-кеш
  const fromLs = lsGet<AreaOption[]>('areas_tree_raw');
  if (fromLs) {
    cachedAreasTreeRaw = fromLs;
    return fromLs;
  }

  if (pendingAreasTreeRawPromise) return pendingAreasTreeRawPromise;

  const request = async () => {
    isLoadingAreasTreeRaw = true;
    try {
      const response = await api.get('/hh/dictionaries/areas', { params: { locale: 'RU' } });
      const json = response.data;
      const items = Array.isArray(json.items) ? json.items : [];
      cachedAreasTreeRaw = preserveAreasTree(items);
      lsSet('areas_tree_raw', cachedAreasTreeRaw);
      return cachedAreasTreeRaw;
    } finally {
      isLoadingAreasTreeRaw = false;
      pendingAreasTreeRawPromise = null;
    }
  };

  pendingAreasTreeRawPromise = request();
  return pendingAreasTreeRawPromise;
};

export const fetchHHAreasTree = async (): Promise<AreaOption[]> => {
  if (cachedAreasTree) return cachedAreasTree;
  if (pendingAreasTreePromise) return pendingAreasTreePromise;

  const request = async () => {
    isLoadingAreasTree = true;
    try {
      const raw = await fetchHHAreasTreeRaw();
      const copy = JSON.parse(JSON.stringify(raw)) as AreaOption[];
      cachedAreasTree = normalizeAreasHierarchy(copy);
      return cachedAreasTree;
    } finally {
      isLoadingAreasTree = false;
      pendingAreasTreePromise = null;
    }
  };

  pendingAreasTreePromise = request();
  return pendingAreasTreePromise;
};

const flattenIndustries = (items: any[]): IndustryOption[] => {
  const result: IndustryOption[] = [];
  const traverse = (nodes: any[]) => {
    nodes?.forEach((node) => {
      if (node && typeof node.id !== 'undefined' && node.name) {
        result.push({ id: String(node.id), name: String(node.name) });
      }
      if (Array.isArray(node.industries) && node.industries.length > 0) {
        traverse(node.industries);
      }
    });
  };
  traverse(items);
  return result;
};

const preserveIndustriesTree = (items: any[]): IndustryOption[] => {
  return items.map((item) => ({
    id: String(item.id),
    name: String(item.name),
    industries: Array.isArray(item.industries) && item.industries.length > 0 
      ? preserveIndustriesTree(item.industries) 
      : undefined
  }));
};

export const fetchHHIndustries = async (): Promise<IndustryOption[]> => {
  if (cachedIndustries) {
    return cachedIndustries;
  }

  const fromLs = lsGet<IndustryOption[]>('industries');
  if (fromLs) {
    cachedIndustries = fromLs;
    return fromLs;
  }

  if (pendingIndustriesPromise) {
    return pendingIndustriesPromise;
  }

  const request = async () => {
    isLoadingIndustries = true;
    try {
      const response = await api.get('/hh/dictionaries/industries', { params: { locale: 'RU' } });
      const json = response.data;
      const items = Array.isArray(json.items) ? json.items : [];
      const industries = preserveIndustriesTree(items);
      cachedIndustries = industries;
      lsSet('industries', industries);
      return industries;
    } finally {
      isLoadingIndustries = false;
      pendingIndustriesPromise = null;
    }
  };

  pendingIndustriesPromise = request();
  return pendingIndustriesPromise;
};

const preserveSpecializationsTree = (items: any[]): SpecializationOption[] => {
  return items.map((item) => ({
    id: String(item.id),
    name: String(item.name),
    roles: Array.isArray(item.roles) && item.roles.length > 0 
      ? preserveSpecializationsTree(item.roles) 
      : undefined
  }));
};

export const fetchHHSpecializations = async (): Promise<SpecializationOption[]> => {
  if (cachedSpecializations) {
    return cachedSpecializations;
  }

  const fromLs = lsGet<SpecializationOption[]>('specializations');
  if (fromLs) {
    cachedSpecializations = fromLs;
    return fromLs;
  }

  if (pendingSpecializationsPromise) {
    return pendingSpecializationsPromise;
  }

  const request = async () => {
    isLoadingSpecializations = true;
    try {
      const response = await api.get('/hh/dictionaries/specializations', { params: { locale: 'RU' } });
      const json = response.data;
      const items = Array.isArray(json.items) ? json.items : [];
      const specializations = preserveSpecializationsTree(items);
      cachedSpecializations = specializations;
      lsSet('specializations', specializations);
      return specializations;
    } finally {
      isLoadingSpecializations = false;
      pendingSpecializationsPromise = null;
    }
  };

  pendingSpecializationsPromise = request();
  return pendingSpecializationsPromise;
};

export const fetchHHMetroCities = async (): Promise<MetroCityOption[]> => {
  if (cachedMetroCities) {
    return cachedMetroCities;
  }

  const fromLs = lsGet<MetroCityOption[]>('metro_cities');
  if (fromLs) {
    cachedMetroCities = fromLs;
    return fromLs;
  }

  if (pendingMetroPromise) {
    return pendingMetroPromise;
  }

  const request = async () => {
    isLoadingMetro = true;
    try {
      const response = await api.get('/hh/dictionaries/metro');
      const json = response.data;
      const items = Array.isArray(json.items) ? json.items : [];
      const cities: MetroCityOption[] = items
        .map((city: any) => ({
          id: String(city?.id ?? ''),
          name: String(city?.name ?? ''),
          lines: Array.isArray(city?.lines)
            ? city.lines
                .map((line: any) => ({
                  id: String(line?.id ?? ''),
                  name: String(line?.name ?? ''),
                  hexColor: line?.hexColor ? String(line.hexColor) : undefined,
                  stations: Array.isArray(line?.stations)
                    ? line.stations
                        .map((st: any) => ({
                          id: String(st?.id ?? ''),
                          name: String(st?.name ?? '')
                        }))
                        .filter((st: any) => Boolean(st.id) && Boolean(st.name))
                    : []
                }))
                .filter((line: any) => Boolean(line.id) && Boolean(line.name))
            : []
        }))
        .filter((city: any) => Boolean(city.id) && Boolean(city.name));

      cachedMetroCities = cities;
      lsSet('metro_cities', cities);
      return cities;
    } finally {
      isLoadingMetro = false;
      pendingMetroPromise = null;
    }
  };

  pendingMetroPromise = request();
  return pendingMetroPromise;
};

/**
 * Загрузка справочника графиков работы (work_schedule_by_days) из HH API.
 * Возвращает массив {id, name}, например [{id: 'FIVE_ON_TWO_OFF', name: '5/2'}, ...]
 */
export const fetchHHWorkSchedules = async (): Promise<Array<{id: string; name: string}>> => {
  if (cachedWorkSchedules) return cachedWorkSchedules;

  const fromLs = lsGet<Array<{id: string; name: string}>>('work_schedules');
  if (fromLs) {
    cachedWorkSchedules = fromLs;
    return fromLs;
  }

  if (pendingWorkSchedulesPromise) {
    return pendingWorkSchedulesPromise;
  }

  const request = async () => {
    isLoadingWorkSchedules = true;
    try {
      const response = await api.get('/hh/dictionaries/work-schedules');
      const json = response.data;
      const items: Array<{id: string; name: string}> = Array.isArray(json.items)
        ? json.items
            .map((item: any) => ({
              id: String(item?.id ?? ''),
              name: String(item?.name ?? '')
            }))
            .filter((item: any) => Boolean(item.id) && Boolean(item.name))
        : [];
      cachedWorkSchedules = items;
      lsSet('work_schedules', items);
      return items;
    } finally {
      isLoadingWorkSchedules = false;
      pendingWorkSchedulesPromise = null;
    }
  };

  pendingWorkSchedulesPromise = request();
  return pendingWorkSchedulesPromise;
};

export interface CurrencyOption {
  id: string;
  abbr: string;
  name: string;
  rate: number | null;
  inUse: boolean;
}

export const fetchHHCurrencies = async (): Promise<CurrencyOption[]> => {
  if (cachedCurrencies) return cachedCurrencies;

  const fromLs = lsGet<CurrencyOption[]>('currencies');
  if (fromLs) {
    cachedCurrencies = fromLs;
    return fromLs;
  }

  if (pendingCurrenciesPromise) {
    return pendingCurrenciesPromise;
  }

  const request = async () => {
    isLoadingCurrencies = true;
    try {
      const response = await api.get('/hh/dictionaries/currencies');
      const json = response.data;
      const items: CurrencyOption[] = Array.isArray(json.items)
        ? json.items
            .map((item: any) => ({
              id: String(item?.id ?? item?.abbr ?? ''),
              abbr: String(item?.abbr ?? item?.id ?? ''),
              name: String(item?.name ?? ''),
              rate: item?.rate ?? null,
              inUse: item?.inUse !== false,
            }))
            .filter((item: any) => Boolean(item.id) && Boolean(item.name))
        : [];
      cachedCurrencies = items;
      lsSet('currencies', items);
      return items;
    } finally {
      isLoadingCurrencies = false;
      pendingCurrenciesPromise = null;
    }
  };

  pendingCurrenciesPromise = request();
  return pendingCurrenciesPromise;
};

export const clearHHAreasCache = () => {
  if (!isLoading) {
    cachedAreas = null;
    localStorage.removeItem(LS_PREFIX + 'areas_flat');
  }
};

export const clearHHAreasTreeCache = () => {
  if (!isLoadingAreasTree) cachedAreasTree = null;
  if (!isLoadingAreasTreeRaw) {
    cachedAreasTreeRaw = null;
    localStorage.removeItem(LS_PREFIX + 'areas_tree_raw');
  }
};

export const clearHHIndustriesCache = () => {
  if (!isLoadingIndustries) {
    cachedIndustries = null;
    localStorage.removeItem(LS_PREFIX + 'industries');
  }
};

export const clearHHSpecializationsCache = () => {
  if (!isLoadingSpecializations) {
    cachedSpecializations = null;
    cachedSpecializationPathNameIndex = null;
    localStorage.removeItem(LS_PREFIX + 'specializations');
  }
};

export const clearHHMetroCache = () => {
  if (!isLoadingMetro) {
    cachedMetroCities = null;
    localStorage.removeItem(LS_PREFIX + 'metro_cities');
  }
};

/**
 * Find industry ID by name (fuzzy match). Used when AI generates a text name
 * that needs to be mapped to an actual HH industry ID.
 */
export const findIndustryIdByName = async (name: string): Promise<string | null> => {
  try {
    const industries = await fetchHHIndustries();
    const normalized = name.trim().toLowerCase();

    const search = (items: IndustryOption[]): string | null => {
      for (const item of items) {
        if (item.name.toLowerCase() === normalized) {
          return item.id;
        }
        if (item.industries) {
          const found = search(item.industries);
          if (found) return found;
        }
      }
      return null;
    };

    // Exact match first
    const exact = search(industries);
    if (exact) return exact;

    // Partial match: AI name contains or is contained in dictionary name
    const searchPartial = (items: IndustryOption[]): string | null => {
      for (const item of items) {
        const itemNameLower = item.name.toLowerCase();
        if (itemNameLower.includes(normalized) || normalized.includes(itemNameLower)) {
          return item.id;
        }
        if (item.industries) {
          const found = searchPartial(item.industries);
          if (found) return found;
        }
      }
      return null;
    };

    return searchPartial(industries);
  } catch (error) {
    console.error('Error finding industry by name:', error);
    return null;
  }
};

/**
 * Find specialization path key by name (fuzzy match). Used when AI generates
 * a text name that needs to be mapped to an actual HH specialization.
 */
export const findSpecializationPathKeyByName = async (name: string): Promise<string | null> => {
  try {
    const map = await getSpecializationPathNameIndex();
    const normalized = name.trim().toLowerCase();

    // Exact match
    const entries = Array.from(map.entries());
    for (const [key, val] of entries) {
      if (val.toLowerCase() === normalized) return key;
    }

    // Partial match
    for (const [key, val] of entries) {
      const valLower = val.toLowerCase();
      if (valLower.includes(normalized) || normalized.includes(valLower)) return key;
    }

    return null;
  } catch (error) {
    console.error('Error finding specialization by name:', error);
    return null;
  }
};

export const getIndustryNameById = async (industryId: string): Promise<string> => {
  try {
    const industries = await fetchHHIndustries();
    const normalizedId = String(industryId).trim();
    
    const findIndustryName = (items: IndustryOption[], id: string): string | null => {
      for (const item of items) {
        if (String(item.id) === id) {
          return item.name;
        }
        if (item.industries) {
          const found = findIndustryName(item.industries, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const name = findIndustryName(industries, normalizedId);
    return name || industryId;
  } catch (error) {
    console.error('Error fetching industry name:', error);
    return industryId;
  }
};

// Функция для получения названия области по ID
export const getAreaNameById = async (areaId: string): Promise<string> => {
  try {
    const normalizedId = String(areaId).trim();
    const countryNameById: Record<string, string> = {
      '113': 'Россия',
      '40': 'Казахстан',
      '9': 'Азербайджан',
      '16': 'Беларусь',
      '28': 'Грузия',
      '48': 'Кыргызстан',
      '97': 'Узбекистан',
    };
    if (countryNameById[normalizedId]) {
      return countryNameById[normalizedId];
    }
    const areas = await fetchHHAreasTree();
    
    // Рекурсивный поиск по дереву областей
    const findAreaName = (items: AreaOption[], id: string): string | null => {
      for (const item of items) {
        if (item.id === id) {
          return item.name;
        }
        if (item.areas) {
          const found = findAreaName(item.areas, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const name = findAreaName(areas, normalizedId);
    return name || normalizedId;
  } catch (error) {
    console.error('Error fetching area name:', error);
    return areaId;
  }
};

export const getAreaIdByName = async (areaName: string): Promise<string | null> => {
  try {
    const areas = await fetchHHAreasTree();
    const target = areaName.trim().toLowerCase();

    const findAreaId = (items: AreaOption[]): string | null => {
      for (const item of items) {
        if (item.name.trim().toLowerCase() === target) {
          return String(item.id);
        }
        if (item.areas) {
          const found = findAreaId(item.areas);
          if (found) return found;
        }
      }
      return null;
    };

    return findAreaId(areas);
  } catch (error) {
    console.error('Error fetching area id by name:', error);
    return null;
  }
};

export const getSpecializationNameById = async (specializationId: string): Promise<string> => {
  try {
    const specializations = await fetchHHSpecializations();
    const normalizedId = String(specializationId).trim();

    const findBest = (items: SpecializationOption[], id: string, depth: number): { name: string; depth: number } | null => {
      let best: { name: string; depth: number } | null = null;
      for (const item of items) {
        if (item.roles && item.roles.length > 0) {
          const childBest = findBest(item.roles, id, depth + 1);
          if (childBest && (!best || childBest.depth > best.depth)) best = childBest;
        }
        if (String(item.id) === id) {
          const cand = { name: item.name, depth };
          if (!best || cand.depth > best.depth) best = cand;
        }
      }
      return best;
    };

    const best = findBest(specializations, normalizedId, 0);
    return best?.name || specializationId;
  } catch (error) {
    console.error('Error fetching specialization name:', error);
    return specializationId;
  }
};

type SpecializationPathNameIndex = Map<string, string>;
let cachedSpecializationPathNameIndex: SpecializationPathNameIndex | null = null;

const getSpecializationPathNameIndex = async (): Promise<SpecializationPathNameIndex> => {
  if (cachedSpecializationPathNameIndex) return cachedSpecializationPathNameIndex;
  const specializations = await fetchHHSpecializations();
  const map: SpecializationPathNameIndex = new Map<string, string>();

  const walk = (items: SpecializationOption[], prefix: string) => {
    for (const item of items) {
      const key = prefix ? `${prefix}/${item.id}` : String(item.id);
      map.set(key, String(item.name));
      if (item.roles && item.roles.length > 0) {
        walk(item.roles, key);
      }
    }
  };

  walk(specializations, '');
  cachedSpecializationPathNameIndex = map;
  return map;
};

export const getSpecializationNameByPathKey = async (pathKey: string): Promise<string> => {
  const key = String(pathKey).trim();
  if (!key) return '';
  const map = await getSpecializationPathNameIndex();
  return map.get(key) || key;
};

/**
 * По leaf ID специализации (например '96') найти полный path key (например '1/96').
 * Нужно при импорте из HH, где API возвращает только числовой ID роли.
 */
export const findSpecializationPathKeyById = async (leafId: string): Promise<string> => {
  const id = String(leafId).trim();
  if (!id) return '';
  const map = await getSpecializationPathNameIndex();
  // Полный путь "X/Y" сразу — это уже path key, ничего искать не надо.
  if (id.includes('/') && map.has(id)) return id;
  // Голый числовой ID — приоритет роли (path "X/id") над категорией с тем же id.
  // В HH-классификаторе номера ролей и категорий пересекаются (~27 коллизий, например
  // id=4 — это и категория "Финансы", и роль "Автомойщик" внутри категории "Транспорт"
  // под path "19/4"). Если просто вернуть совпадение по голому id, попадаем в категорию
  // вместо роли и пользователь видит неверное название специализации.
  const keys = Array.from(map.keys());
  for (let i = 0; i < keys.length; i++) {
    const parts = keys[i].split('/');
    if (parts.length > 1 && parts[parts.length - 1] === id) {
      return keys[i];
    }
  }
  // Роли с таким id нет — возвращаем как есть (вероятно категория или незнакомый id).
  if (map.has(id)) return id;
  return id;
};

export const getStoredSpecializationName = async (specializationId: string): Promise<string> => {
  const key = String(specializationId).trim();
  if (!key) return '';

  const directName = await getSpecializationNameByPathKey(key);
  if (directName && directName !== key) return directName;

  if (/^\d+$/.test(key)) {
    const pathKey = await findSpecializationPathKeyById(key);
    if (pathKey && pathKey !== key) {
      const legacyName = await getSpecializationNameByPathKey(pathKey);
      if (legacyName && legacyName !== pathKey) return legacyName;
    }
  }

  return key;
};

/**
 * Нормализует ID специализации: если передан голый числовой ID (старый формат),
 * резолвит его в полный path key (например "11/10").
 * Если ID уже содержит "/" или не числовой — возвращает как есть.
 */
export const resolveSpecializationId = async (id: string): Promise<string> => {
  const normalized = String(id).trim();
  if (/^\d+$/.test(normalized)) {
    const pathKey = await findSpecializationPathKeyById(normalized);
    if (pathKey && pathKey !== normalized) return pathKey;
  }
  return normalized;
};

// Проверяет, является ли ID специализации листовым (без дочерних roles)
// Поддерживает path key формат: "11/124" → пробует "11/124", затем "124"
export const isSpecializationLeafId = async (specId: string): Promise<boolean> => {
  const tree = await fetchHHSpecializations();
  const raw = String(specId).trim();
  const findByPath = (items: SpecializationOption[], pathKey: string): SpecializationOption | null => {
    const parts = pathKey.split('/').filter(Boolean);
    if (parts.length === 0) return null;

    let currentItems = items;
    let currentNode: SpecializationOption | null = null;

    for (const part of parts) {
      currentNode = currentItems.find((item) => String(item.id) === part) || null;
      if (!currentNode) return null;
      currentItems = currentNode.roles || [];
    }

    return currentNode;
  };
  const find = (items: SpecializationOption[], targetId: string): boolean | null => {
    for (const item of items) {
      if (String(item.id) === targetId) {
        return !item.roles || item.roles.length === 0;
      }
      if (item.roles && item.roles.length > 0) {
        const res = find(item.roles, targetId);
        if (res !== null) return res;
      }
    }
    return null;
  };
  if (raw.includes('/')) {
    const node = findByPath(tree, raw);
    if (node) return !node.roles || node.roles.length === 0;
  }

  // Для голого ID сохраняем старое поведение.
  const result = find(tree, raw);
  return result === true;
};

// Проверяет, является ли ID области листовым городом (без дочерних элементов)
export const isAreaLeafId = async (areaId: string): Promise<boolean> => {
  const tree = await fetchHHAreasTreeRaw();
  const id = String(areaId).trim();
  const find = (items: AreaOption[]): boolean | null => {
    for (const item of items) {
      if (String(item.id) === id) {
        return !item.areas || item.areas.length === 0;
      }
      if (item.areas && item.areas.length > 0) {
        const res = find(item.areas);
        if (res !== null) return res;
      }
    }
    return null;
  };
  const result = find(tree);
  return result === true;
};

export const expandSpecializationIds = async (selectedIds: string[]): Promise<string[]> => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];

  const tree = await fetchHHSpecializations();
  const result = new Set<string>();

  const findNodeByPathKey = (items: SpecializationOption[], pathKey: string): { node: SpecializationOption | null; fullPath: string } => {
    const parts = pathKey.split('/').filter(Boolean);
    if (parts.length === 0) return { node: null, fullPath: '' };

    let current: SpecializationOption[] = items;
    let found: SpecializationOption | null = null;
    let fullPath = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      found = current.find(item => String(item.id) === part) || null;
      if (!found) return { node: null, fullPath: '' };
      
      fullPath = fullPath ? `${fullPath}/${found.id}` : String(found.id);
      current = found.roles || [];
    }

    return { node: found, fullPath };
  };

  const getAllChildPathKeys = (node: SpecializationOption, parentPath: string): string[] => {
    const result: string[] = [];
    const currentPath = parentPath ? `${parentPath}/${node.id}` : String(node.id);

    if (!node.roles || node.roles.length === 0) {
      result.push(currentPath);
    } else {
      for (const child of node.roles) {
        result.push(...getAllChildPathKeys(child, currentPath));
      }
    }

    return result;
  };

  for (const pathKey of selectedIds) {
    const pathKeyStr = String(pathKey);
    const { node, fullPath } = findNodeByPathKey(tree, pathKeyStr);
    
    if (!node) {
      result.add(pathKeyStr);
      continue;
    }

    const hasChildRoles = node.roles && Array.isArray(node.roles) && node.roles.length > 0;
    
    if (hasChildRoles) {
      const parentPath = fullPath.split('/').slice(0, -1).join('/');
      const childPathKeys = getAllChildPathKeys(node, parentPath);
      childPathKeys.forEach(id => result.add(id));
    } else {
      result.add(pathKeyStr);
    }
  }

  return Array.from(result);
};

const KNOWN_COUNTRY_IDS = new Set(['113', '40', '9', '16', '28', '48', '97', '1001']);

const compressWithTree = (selectedIds: string[], tree: AreaOption[]): string[] => {
  const selectedSet = new Set<string>(selectedIds.map(String));

  const idToNode = new Map<string, AreaOption>();
  const idToCountryId = new Map<string, string | undefined>();
  const rootIdsByCountry = new Map<string, Set<string>>();
  const leafIdsByCountry = new Map<string, Set<string>>();

  const buildMap = (items: AreaOption[]) => {
    for (const item of items) {
      idToNode.set(String(item.id), item);
      if (item.countryId) {
        const countryId = String(item.countryId);
        const existing = rootIdsByCountry.get(countryId) || new Set<string>();
        existing.add(String(item.id));
        rootIdsByCountry.set(countryId, existing);
      }
      if (item.areas && item.areas.length > 0) buildMap(item.areas);
    }
  };
  buildMap(tree);

  const leafIdsCache = new Map<string, string[]>();
  const selectedDescCache = new Map<string, boolean>();

  const buildCountryIndex = (items: AreaOption[], inheritedCountryId?: string) => {
    for (const item of items) {
      const id = String(item.id);
      const countryId = item.countryId ? String(item.countryId) : inheritedCountryId;
      idToCountryId.set(id, countryId);
      if (!item.areas || item.areas.length === 0) {
        if (countryId) {
          const existing = leafIdsByCountry.get(countryId) || new Set<string>();
          existing.add(id);
          leafIdsByCountry.set(countryId, existing);
        }
      } else {
        buildCountryIndex(item.areas, countryId);
      }
    }
  };
  buildCountryIndex(tree);

  const fullySelectedCountries = new Set<string>();
  Array.from(rootIdsByCountry.entries()).forEach(([countryId, rootIds]) => {
    if (rootIds.size > 0 && Array.from(rootIds).every((id) => selectedSet.has(id))) {
      fullySelectedCountries.add(countryId);
    }
  });
  Array.from(leafIdsByCountry.entries()).forEach(([countryId, leafIds]) => {
    if (leafIds.size > 0 && Array.from(leafIds).every((id) => selectedSet.has(id))) {
      fullySelectedCountries.add(countryId);
    }
  });

  if (fullySelectedCountries.size > 0) {
    Array.from(selectedSet).forEach((id) => {
      const countryId = idToCountryId.get(id);
      if (countryId && fullySelectedCountries.has(countryId)) {
        selectedSet.delete(id);
      }
    });
    Array.from(fullySelectedCountries).forEach((countryId) => selectedSet.add(String(countryId)));
  }

  for (const id of Array.from(selectedSet)) {
    if (KNOWN_COUNTRY_IDS.has(id) && !fullySelectedCountries.has(id)) {
      const countryRoots = rootIdsByCountry.get(id);
      const hasAnyRegionInSelection = countryRoots && Array.from(countryRoots).some((rid) => selectedSet.has(rid));
      if (hasAnyRegionInSelection) {
        selectedSet.delete(id);
      }
    }
  }

  const unknownSelected = Array.from(selectedSet).filter(
    (id) => !idToNode.has(id)
  );

  const collectLeafIds = (node: AreaOption): string[] => {
    const id = String(node.id);
    const cached = leafIdsCache.get(id);
    if (cached) return cached;
    if (!node.areas || node.areas.length === 0) {
      leafIdsCache.set(id, [id]);
      return [id];
    }
    const leaves: string[] = [];
    for (const child of node.areas) leaves.push(...collectLeafIds(child));
    leafIdsCache.set(id, leaves);
    return leaves;
  };

  const hasSelectedDescendant = (node: AreaOption): boolean => {
    const id = String(node.id);
    const cached = selectedDescCache.get(id);
    if (cached !== undefined) return cached;
    if (!node.areas || node.areas.length === 0) {
      selectedDescCache.set(id, false);
      return false;
    }
    for (const child of node.areas) {
      if (selectedSet.has(String(child.id)) || hasSelectedDescendant(child)) {
        selectedDescCache.set(id, true);
        return true;
      }
    }
    selectedDescCache.set(id, false);
    return false;
  };

  const compressNode = (node: AreaOption): string[] => {
    const id = String(node.id);
    const hasChildSelection = hasSelectedDescendant(node);
    const leaves = collectLeafIds(node);
    const allLeavesSelected = leaves.length > 0 && leaves.every((leafId) => selectedSet.has(leafId));

    if ((selectedSet.has(id) && !hasChildSelection) || allLeavesSelected) return [id];
    if (!node.areas || node.areas.length === 0) return selectedSet.has(id) ? [id] : [];

    const out: string[] = [];
    for (const child of node.areas) out.push(...compressNode(child));
    return out;
  };

  const compressedKnown: string[] = [];
  for (const root of tree) compressedKnown.push(...compressNode(root));

  return Array.from(new Set([...unknownSelected, ...compressedKnown].map(String)));
};

export const compressCityIds = async (selectedIds: string[]): Promise<string[]> => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];
  const tree = await fetchHHAreasTreeRaw();
  return compressWithTree(selectedIds, tree);
};

export const compressCityIdsForDisplay = async (selectedIds: string[]): Promise<string[]> => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];

  const rawTree = await fetchHHAreasTreeRaw();
  const rawIdToNode = new Map<string, AreaOption>();
  const buildRawMap = (items: AreaOption[]) => {
    for (const item of items) {
      rawIdToNode.set(String(item.id), item);
      if (item.areas && item.areas.length > 0) buildRawMap(item.areas);
    }
  };
  buildRawMap(rawTree);

  const expandedSet = new Set<string>();
  const expandNode = (node: AreaOption) => {
    expandedSet.add(String(node.id));
    if (node.areas) {
      for (const child of node.areas) expandNode(child);
    }
  };

  for (const id of selectedIds.map(String)) {
    expandedSet.add(id);

    if (KNOWN_COUNTRY_IDS.has(id)) {
      for (const root of rawTree) {
        if (String(root.countryId) === id) expandNode(root);
      }
      continue;
    }

    const node = rawIdToNode.get(id);
    if (node) expandNode(node);
  }

  const normalizedTree = await fetchHHAreasTree();
  return compressWithTree(Array.from(expandedSet), normalizedTree);
};

export interface LanguageOption {
  id: string;
  name: string;
}

let languagesCache: LanguageOption[] = [];
let languagesFetchPromise: Promise<LanguageOption[]> | null = null;

export const fetchHHLanguages = async (): Promise<LanguageOption[]> => {
  if (languagesCache.length > 0) {
    return languagesCache;
  }

  const fromLs = lsGet<LanguageOption[]>('languages');
  if (fromLs) {
    languagesCache = fromLs;
    return fromLs;
  }

  if (languagesFetchPromise) {
    return languagesFetchPromise;
  }

  languagesFetchPromise = (async () => {
    try {
      const response = await api.get('/hh/dictionaries/languages');
      const data = response.data;
      languagesCache = data.items || [];
      lsSet('languages', languagesCache);
      return languagesCache;
    } catch (error) {
      console.error('Error fetching HH languages:', error);
      languagesFetchPromise = null;
      return [];
    }
  })();

  return languagesFetchPromise;
};

export const getLanguageNameById = (id: string): string => {
  if (languagesCache.length === 0) return id;
  const lang = languagesCache.find(l => l.id === id);
  return lang?.name || id;
};

export interface CountryOption {
  id: string;
  name: string;
}

let countriesCache: CountryOption[] = [];
let countriesFetchPromise: Promise<CountryOption[]> | null = null;

export const fetchHHCountries = async (): Promise<CountryOption[]> => {
  if (countriesCache.length > 0) {
    return countriesCache;
  }

  const fromLs = lsGet<CountryOption[]>('countries');
  if (fromLs) {
    countriesCache = fromLs;
    return fromLs;
  }

  if (countriesFetchPromise) {
    return countriesFetchPromise;
  }

  countriesFetchPromise = (async () => {
    try {
      const response = await api.get('/hh/dictionaries/countries');
      const data = response.data;
      countriesCache = data.items || [];
      lsSet('countries', countriesCache);
      return countriesCache;
    } catch (error) {
      console.error('Error fetching HH countries:', error);
      countriesFetchPromise = null;
      return [];
    }
  })();

  return countriesFetchPromise;
};

export const getCountryNameById = (id: string): string => {
  if (countriesCache.length === 0) return id;
  const country = countriesCache.find(c => c.id === id);
  return country?.name || id;
};

/**
 * Resolve a country name to HH.ru country ID by exact dictionary match (case-insensitive).
 * AI is instructed to return full official names ("Россия", "Беларусь", etc.).
 */
export const findCountryIdByName = async (raw: string): Promise<string | null> => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const countries = await fetchHHCountries();
  const lower = trimmed.toLowerCase();
  return countries.find(c => c.name.toLowerCase() === lower)?.id ?? null;
};
